/* AME mega menu behaviour.
 *
 * One state model drives both breakpoints: an item is open or it isn't, and CSS
 * decides whether "open" means a dropped panel or a slid-in drawer pane. The
 * old nav split this across CSS :hover and a JS toggle, which fought each other
 * — a panel opened by hover couldn't be closed by Escape because the CSS rule
 * kept winning.
 *
 * ARIA: this is the WAI-ARIA disclosure navigation pattern, not role="menu".
 * The previous markup claimed role="menu"/"menuitem", which tells a screen
 * reader to expect an application menu with arrow-key item traversal and a
 * focus-trapping model that was never implemented.
 */
(function () {
  var nav = document.querySelector('[data-mega]');
  if (!nav) return;

  var desktop = window.matchMedia('(min-width: 990px)');
  var items = Array.prototype.slice.call(nav.querySelectorAll('[data-mega-item]'));
  var trigger = nav.querySelector('[data-mega-trigger]');
  var scrim = nav.querySelector('[data-mega-scrim]');
  var openItem = null;
  var hoverTimer = null;

  /* Hover intent. Without the open delay, dragging the pointer across the bar
     flashes every panel in turn; without the close delay, the diagonal path
     from a top-level item down into its own panel closes it mid-move. */
  var OPEN_DELAY = 110;
  var CLOSE_DELAY = 220;

  function panelOf(item) { return item.querySelector('[data-mega-panel]'); }
  function toggleOf(item) { return item.querySelector('[data-mega-toggle]'); }

  function open(item) {
    if (openItem === item) return;
    if (openItem) close(openItem);
    item.classList.add('is-open');
    var t = toggleOf(item);
    if (t) t.setAttribute('aria-expanded', 'true');
    openItem = item;
  }

  function close(item) {
    item.classList.remove('is-open');
    var t = toggleOf(item);
    if (t) t.setAttribute('aria-expanded', 'false');
    if (openItem === item) openItem = null;
  }

  function closeAll() {
    items.forEach(close);
    openItem = null;
  }

  /* ── Drawer (mobile) ── */
  var scrollY = 0;

  function openDrawer() {
    scrollY = window.scrollY;
    nav.classList.add('is-drawer-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollY + 'px';
    document.body.style.width = '100%';
  }

  function closeDrawer() {
    if (!nav.classList.contains('is-drawer-open')) return;
    nav.classList.remove('is-drawer-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    closeAll();
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  if (trigger) {
    trigger.addEventListener('click', function () {
      if (nav.classList.contains('is-drawer-open')) { closeDrawer(); }
      else { openDrawer(); }
    });
  }

  if (scrim) scrim.addEventListener('click', closeDrawer);

  var closeBtn = nav.querySelector('[data-mega-close]');
  if (closeBtn) {
    closeBtn.addEventListener('click', function () {
      closeDrawer();
      if (trigger) trigger.focus();
    });
  }

  /* ── Per-item wiring ── */
  items.forEach(function (item) {
    var toggle = toggleOf(item);
    var panel = panelOf(item);
    if (!toggle || !panel) return;

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      if (item.classList.contains('is-open')) { close(item); }
      else { open(item); }
    });

    var back = panel.querySelector('[data-mega-back]');
    if (back) {
      back.addEventListener('click', function () {
        close(item);
        toggle.focus();
      });
    }

    /* Hover is desktop-only; the media query is read at event time so a resize
       never leaves stale listeners attached. */
    item.addEventListener('mouseenter', function () {
      if (!desktop.matches) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () { open(item); }, OPEN_DELAY);
    });

    item.addEventListener('mouseleave', function () {
      if (!desktop.matches) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () { close(item); }, CLOSE_DELAY);
    });

    /* Focus leaving the whole item closes it, so tabbing past a panel doesn't
       leave it hanging open behind the next one. */
    item.addEventListener('focusout', function (e) {
      if (!desktop.matches) return;
      if (!item.contains(e.relatedTarget)) close(item);
    });
  });

  /* ── Keyboard ── */
  nav.addEventListener('keydown', function (e) {
    var item = e.target.closest('[data-mega-item]');

    if (e.key === 'Escape') {
      if (openItem) {
        var t = toggleOf(openItem);
        close(openItem);
        if (t) t.focus();
        e.preventDefault();
      } else if (nav.classList.contains('is-drawer-open')) {
        closeDrawer();
        if (trigger) trigger.focus();
        e.preventDefault();
      }
      return;
    }

    if (!item) return;
    var idx = items.indexOf(item);

    if (e.key === 'ArrowDown') {
      // Open and step into the panel — the standard disclosure-nav shortcut.
      if (e.target.hasAttribute('data-mega-toggle') || e.target.hasAttribute('data-mega-top')) {
        /* ★ a DIRECT-LINK item (Parts) has no panel; ArrowDown on it must be a no-op, not a
         * TypeError that kills every later keydown handler on the bar. */
        if (!panelOf(item)) return;
        e.preventDefault();
        open(item);
        // The first focusable in panel order is the back button, which is
        // display:none above 990px — focusing it silently does nothing and
        // strands the user on the bar. Take the first one actually rendered.
        var candidates = panelOf(item).querySelectorAll('a[href], button');
        for (var i = 0; i < candidates.length; i++) {
          if (candidates[i].getClientRects().length) { candidates[i].focus(); break; }
        }
      }
      return;
    }

    if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') &&
        (e.target.hasAttribute('data-mega-toggle') || e.target.hasAttribute('data-mega-top'))) {
      e.preventDefault();
      var step = e.key === 'ArrowRight' ? 1 : -1;
      var next = items[(idx + step + items.length) % items.length];
      var link = next.querySelector('[data-mega-top]');
      if (link) link.focus();
      if (openItem) open(next);
    }
  });

  document.addEventListener('click', function (e) {
    if (nav.contains(e.target)) return;
    if (desktop.matches) closeAll();
  });
  // 2026-10-02: iOS dispatches no click for a tap on a non-interactive area, so the panel stayed open on iPads
  document.addEventListener('touchstart', function (e) {
    if (nav.contains(e.target)) return;
    if (desktop.matches) closeAll();
  }, { passive: true });

  /* Crossing the breakpoint resets everything — a drawer left open while the
     viewport widens would otherwise strand body{position:fixed}. */
  var onBreakpoint = function () {
    closeAll();
    closeDrawer();
  };
  if (desktop.addEventListener) { desktop.addEventListener('change', onBreakpoint); }
  else { desktop.addListener(onBreakpoint); }
})();
